import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { recordCode } from '../../form/header/recordCode';
import { historyLinkTarget } from '../historyLink';
import { useAgo } from '../useAgo';
import type { IHistoryRowProps } from './IHistoryRowProps';
import { isLongChange } from './historyKinds';
import { useHistoryText } from './useHistoryText';

/** Позначка місця номера іншого запису в підписі події зв'язку. */
const MARK = '\u0000';

/**
 * Рядок історії (прототип v4): аватар 26px (нейтральний), хто 600, дія; зміна — старе значення закреслене й приглушене → нове 500
 * (підписи статусів, варіантів і осіб; довгий текст — «змінено»); файл — його назва; зв'язок — «пов’язано з 9» / «зв’язок з 9 вилучено»,
 * номер — посилання на інший запис тим самим текстом (запис видалено — «(видалений запис)»); коментар — у рамці; час праворуч
 * (точний — у підказці).
 */
export function HistoryRow({ entry, valueText, typeName }: IHistoryRowProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const ago = useAgo();
  const { lead } = useHistoryText(typeName);
  const target = historyLinkTarget(entry);
  const [before, after] = target ? lead(entry, MARK).split(MARK) : [lead(entry), undefined];
  const change = (entry.kind === 'changed' || entry.kind === 'status_changed' || entry.kind === 'assigned') && (entry.from != null || entry.to != null);
  return (
    <div className="rtab-hist">
      <span className="rtab-hist__avatar" aria-hidden>{entry.actor?.initials ?? '?'}</span>
      <div className="rtab-hist__body">
        <div className="rtab-hist__line">
          <b>{entry.actor?.name ?? '—'}</b>
          {target
            ? <span>{before}<Link className="rtab-hist__ref" to={routes.record(target.module, target.recordType, target.id)} title={target.title}>{recordCode(target.display)}</Link>{after}</span>
            : <span>{before}</span>}
          {change && (isLongChange(entry)
            ? <span className="rtab-hist__muted">{t('recordPage.history.edited')}</span>
            : <><span className="rtab-hist__from">{valueText(entry, entry.from)}</span><span className="rtab-hist__muted">→</span><span className="rtab-hist__to">{valueText(entry, entry.to)}</span></>)}
          {entry.kind === 'file_added' && entry.to && <span className="rtab-hist__to">{entry.to}</span>}
          {entry.kind === 'file_removed' && entry.from && <span className="rtab-hist__from">{entry.from}</span>}
          {entry.mergedCount > 1 && <span className="rtab-hist__muted">{t('history.merged', { count: entry.mergedCount })}</span>}
          <span className="rtab-hist__time" title={formatDateTime(entry.at, lang)}>{ago(entry.at)}</span>
        </div>
        {entry.kind === 'comment' && entry.to && <div className="rtab-hist__quote">{entry.to}</div>}
      </div>
    </div>
  );
}
